"use client";

import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { User } from "@supabase/supabase-js";
import type { LucideIcon } from "lucide-react";
import {
	FlagIcon,
	Link2Icon,
	MoreVerticalIcon,
	PencilIcon,
	TrashIcon,
} from "lucide-react";
import React from "react";
import type { CommentTreeNode } from "./comments-box";

export interface menuItem {
	label: string;
	icon?: LucideIcon;
	group?: string;
	kbd?: string;
}

const menuOptions: menuItem[] = [
	{
		label: "Edit",
		icon: PencilIcon,
		group: "Comment",
		kbd: "⌘E",
	},
	{
		label: "Delete",
		icon: TrashIcon,
		group: "Comment",
		kbd: "⌘D",
	},
	{
		label: "Report",
		icon: FlagIcon,
		group: "Help",
		kbd: "⌘R",
	},
	{
		label: "Copy Link",
		icon: Link2Icon,
		group: "Help",
		kbd: "⌘C",
	},
];

interface CommentPostOptionsProps {
	className?: string;
	comment: CommentTreeNode;
	currentUser: User | null;
	onEdit: (e: React.MouseEvent) => void;
	onCopy: (e: React.MouseEvent) => void;
	onReport: (e: React.MouseEvent) => void;
	onDelete: (e: React.MouseEvent) => void;
}

export const CommentPostOptions = (props: CommentPostOptionsProps) => {
	const getOnClick = (label: string) => {
		switch (label) {
			case "Edit":
				return props.onEdit;
			case "Delete":
				return props.onDelete;
			case "Report":
				return props.onReport;
			case "Copy Link":
				return props.onCopy;
		}
	};
	const checkPermission = (label: string) => {
		switch (label) {
			case "Edit":
				return props.currentUser?.id === props.comment.user_id;
			case "Delete":
				return props.currentUser?.id === props.comment.user_id;
			case "Report":
				return true;
			case "Copy Link":
				return true;
		}
	};
	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				asChild
				className={cn("flex items-center justify-center", props.className)}
			>
				<Button
					variant="outline"
					className="flex border-none shadow-none items-center justify-center p-2.5 rounded-full"
				>
					<MoreVerticalIcon className="size-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-42 mr-2" align="end">
				{menuOptions.map((item, index) =>
					checkPermission(item.label) ? (
						<React.Fragment key={`fragment-${item.label}-${index}`}>
							<DropdownMenuItem
								key={`item-${item.label}`}
								onClick={getOnClick(item.label)}
							>
								{item.icon && (
									<item.icon className="size-4 fill-white/30 group-hover:stroke-[3px]" />
								)}
								{item.label}
								<DropdownMenuShortcut>{item.kbd}</DropdownMenuShortcut>
							</DropdownMenuItem>
						</React.Fragment>
					) : null,
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
